import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import type { Tenant } from "@vereinfach/types";
import { api, apiUrl } from "../lib/api";
import { useImpersonation } from "../lib/impersonation";
import { formatDate } from "../lib/format";

type AdminTenantRow = Tenant & {
  owner_email: string | null;
  owner_name: string | null;
};

export function AdminDashboard() {
  const [tenants, setTenants] = useState<AdminTenantRow[] | null>(null);
  const [nuking, setNuking] = useState(false);
  const { setActiveTenantId } = useImpersonation();
  const navigate = useNavigate();

  async function refresh() {
    const { tenants } = await api<{ tenants: AdminTenantRow[] }>("/_admin/tenants");
    setTenants(tenants);
  }

  useEffect(() => {
    refresh();
  }, []);

  function manage(tenantId: string) {
    setActiveTenantId(tenantId);
    navigate({ to: "/" });
  }

  async function runNuke() {
    const phrase = prompt(
      'Das löscht JEDE Organisation, jedes Mitglied, jede Rechnung und jedes PDF — system-weit.\n\nTippe "nuke" zum Bestätigen.',
    );
    if (phrase?.trim() !== "nuke") return;
    setNuking(true);
    try {
      const res = await fetch(apiUrl("/_admin/nuke"), {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // Everything we had in hand is gone. Bounce to the landing so
      // the app re-bootstraps with a clean slate.
      window.location.href = "/";
    } catch (err) {
      alert("Nuke fehlgeschlagen: " + (err as Error).message);
      setNuking(false);
    }
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Admin</h1>
          <p className="text-sm text-slate-500">
            System-weite Verwaltung. Nur für Admin-Accounts sichtbar.
          </p>
        </div>
      </div>

      <section className="mb-10">
        <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">
          Alle Organisationen
        </h2>
        {tenants === null ? (
          <p className="text-slate-400">Lädt…</p>
        ) : tenants.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-slate-500">
            Keine Organisationen vorhanden.
          </div>
        ) : (
          <div className="rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="text-left font-medium px-4 py-2.5">Name</th>
                  <th className="text-left font-medium px-4 py-2.5">Slug</th>
                  <th className="text-left font-medium px-4 py-2.5 hidden md:table-cell">
                    Besitzer
                  </th>
                  <th className="text-left font-medium px-4 py-2.5 hidden lg:table-cell">
                    Erstellt
                  </th>
                  <th className="px-4 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {tenants.map((t) => (
                  <tr key={t.id} className="border-t border-slate-100">
                    <td className="px-4 py-2.5 font-medium">{t.name}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-slate-500">{t.slug}</td>
                    <td className="px-4 py-2.5 text-slate-500 hidden md:table-cell">
                      {t.owner_email ?? "—"}
                    </td>
                    <td className="px-4 py-2.5 text-slate-500 hidden lg:table-cell">
                      {formatDate(t.created_at)}
                    </td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      <button
                        onClick={() => manage(t.id)}
                        className="text-sm text-slate-900 hover:underline"
                      >
                        Verwalten ›
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">
          Gefahrenzone
        </h2>
        <div className="rounded-xl border border-red-200 bg-red-50/50 p-5">
          <p className="text-sm font-medium text-red-900">Alle Daten löschen (system-weit)</p>
          <p className="mt-1 text-xs text-red-800/80">
            Löscht jede Organisation, jedes Mitglied, jede Rechnung, jede Datei in R2 und jede
            aktive Session. Für Entwicklung und Tests.
          </p>
          <button
            onClick={runNuke}
            disabled={nuking}
            className="mt-4 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
          >
            {nuking ? "Lösche…" : "Alles löschen"}
          </button>
        </div>
      </section>
    </div>
  );
}
